import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth, can, rolesOf } from "@/context/AuthContext";
import { FIRM } from "@/lib/firm";
import {
  LayoutDashboard, School, BookOpen, ClipboardList, FileText, PackageCheck, FileWarning,
  Boxes, FlaskConical, Truck, Layers, ScanLine, BarChart3, LogOut, Menu, X,
  Building2, PauseCircle, ShoppingCart, PackageOpen, AlarmClock, KeyRound, FileSpreadsheet,
  Database, ListTree, Settings, ChevronRight, CheckSquare, ScrollText,
  ArrowRightLeft, ShieldCheck, History, PencilRuler, SlidersHorizontal, ReceiptText,
} from "lucide-react";
import { GlobalSearch } from "@/components/GlobalSearch";
import { SessionPicker, SessionBanner } from "@/components/SessionPicker";
import { useFeatures } from "@/lib/useFeatures";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { statusLabel, statusPill } from "@/lib/status";

const ChangePasswordDialog = ({ open, setOpen }) => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (form.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (form.new_password !== form.confirm) return toast.error("Both new passwords must match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password, new_password: form.new_password,
      });
      toast.success("Password changed");
      setOpen(false); setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-[3px] max-w-sm" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-black">Change my password</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {[["current_password", "Current password"], ["new_password", "New password"], ["confirm", "Confirm new password"]].map(([k, l]) => (
            <div key={k}>
              <Label className="label-xs">{l}</Label>
              <Input data-testid={`cp-${k.replace("_", "-")}-input`} type="password" className="mt-1.5 rounded-[2px]"
                value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                autoComplete={k === "current_password" ? "current-password" : "new-password"} />
            </div>
          ))}
        </div>
        <Button data-testid="save-password-btn" disabled={saving} onClick={save}
          className="btn-primary">
          {saving ? "Saving…" : "Update password"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const NAV = [
  { group: null, items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
    { to: "/my-work", label: "My Work", icon: CheckSquare, key: "tasks", badge: true },
  ] },
  {
    group: "Demand", icon: ClipboardList, items: [
      { to: "/booklists", label: "Booklists", icon: ClipboardList, key: "booklists" },
      { to: "/stationery-lists", label: "Stationery Lists", icon: PencilRuler, key: "stationery" },
      { to: "/sales-orders", label: "Sales Orders", icon: ShoppingCart, key: "sales-orders" },
      { to: "/pending-billing", label: "Pending Billing", icon: ReceiptText, key: "sales-orders" },
      { to: "/pending-report", label: "Pending & Demand Report", icon: BarChart3, key: "reports" },
      { to: "/po-billship", label: "Bill To / Ship To", icon: FileSpreadsheet, key: "reports" },
      { to: "/reallocation", label: "Stock Reallocation", icon: ArrowRightLeft, key: "reallocation" },
    ],
  },
  {
    group: "Purchase", icon: FileText, items: [
      { to: "/purchase-orders", label: "Purchase Orders", icon: FileText, key: "purchase-orders" },
      { to: "/receipts", label: "Goods Receipt (GRN)", icon: PackageCheck, key: "receipts" },
      { to: "/claims", label: "Claim Letters", icon: FileWarning, key: "claims" },
    ],
  },
  {
    group: "Warehouse", icon: Boxes, items: [
      { to: "/stock", label: "Stock", icon: Boxes, key: "stock" },
      { to: "/stock-adjustment", label: "Stock Adjustment", icon: SlidersHorizontal, key: "stock-adjustment", adminOnly: true },
      { to: "/bundling", label: "Set Bundling", icon: PackageOpen, key: "bundling" },
      { to: "/packing", label: "Packing Scan", icon: ScanLine, key: "packing" },
      { to: "/samples", label: "Samples & Damage", icon: FlaskConical, key: "samples" },
    ],
  },
  {
    group: "Dispatch", icon: Truck, items: [
      { to: "/shipments", label: "Dispatch Boxes", icon: Truck, key: "shipments" },
      { to: "/batches", label: "Batch Dispatch", icon: Layers, key: "batches" },
      { to: "/cover-letters", label: "Stock Receiving Letters", icon: FileText, key: "cover-letters" },
      { to: "/scan", label: "Scan Box", icon: ScanLine, key: "scan" },
      { to: "/publisher-returns", label: "Publisher Returns", icon: PackageOpen, key: "publisher-returns", feature: "publisherReturns" },
    ],
  },
  {
    group: "Masters", icon: Database, items: [
      { to: "/schools", label: "Schools", icon: School, key: "schools" },
      { to: "/parties", label: "B2B Parties", icon: Building2, key: "parties" },
      { to: "/publishers", label: "Publishers", icon: Building2, key: "publishers" },
      { to: "/books", label: "Book Master", icon: BookOpen, key: "books" },
      { to: "/price-history", label: "Price History", icon: History, key: "books" },
      { to: "/data", label: "Sessions & Masters", icon: ListTree, key: "data" },
    ],
  },
  {
    group: "Admin", icon: Settings, items: [
      { to: "/logs", label: "Activity Log", icon: ScrollText, key: "logs", adminOnly: true },
      { to: "/data-audit", label: "Data Audit", icon: ShieldCheck, key: "data-audit", adminOnly: true },
      { to: "/settings", label: "Settings", icon: Settings, key: "settings", adminOnly: true },
    ],
  },
  { group: null, items: [
    { to: "/manual", label: "Manual", icon: BookOpen, key: "dashboard" },
  ] },
];

const SidebarNav = ({ user, pathname, onNavigate }) => {
  const [pendingTasks, setPendingTasks] = useState(0);
  const features = useFeatures();

  // the badge refreshes when the window regains focus, not on every navigation
  useEffect(() => {
    const pull = () => api.get("/tasks/summary").then(({ data }) => setPendingTasks(data.pending)).catch(() => {});
    pull();
    window.addEventListener("focus", pull);
    return () => window.removeEventListener("focus", pull);
  }, []);

  const groups = NAV.map((g) => ({
    ...g,
    items: g.items.filter((n) => (n.feature ? features[n.feature] : true))
      .filter((n) => (n.adminOnly ? rolesOf(user).includes("admin") : can(user, n.key))),
  })).filter((g) => g.items.length);

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("bf_nav_open") || "null");
      if (saved) return saved;
    } catch (e) { console.warn("Saved sidebar state unreadable; using defaults", e); }
    return groups.filter((g) => g.group).map((g) => g.group);
  });

  const toggle = (name) => {
    const next = openGroups.includes(name) ? openGroups.filter((g) => g !== name) : [...openGroups, name];
    setOpenGroups(next);
    localStorage.setItem("bf_nav_open", JSON.stringify(next));
  };

  const link = (n) => {
    const active = pathname === n.to || (n.to !== "/" && pathname.startsWith(n.to));
    return (
      <Link
        key={n.to}
        to={n.to}
        onClick={onNavigate}
        data-testid={`nav-${n.key === "reports" ? "demand" : n.key}`}
        className={`flex items-center gap-3 pl-5 pr-4 py-2 text-sm border-l-2 transition-colors duration-150 ${
          active
            ? "border-[#0055FF] bg-white/10 text-white font-semibold"
            : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
        }`}
      >
        <n.icon size={15} />
        {n.label}
        {n.badge && pendingTasks > 0 && (
          <span data-testid="nav-tasks-badge"
            className="ml-auto mono text-[10px] font-bold px-1.5 py-0.5 bg-[#E53E3E] text-white">
            {pendingTasks}
          </span>
        )}
      </Link>
    );
  };

  return (
    <nav className="flex-1 overflow-y-auto py-3">
      {groups.map((g) => {
        if (!g.group) return g.items.map(link);
        const hasActive = g.items.some((n) => pathname === n.to || (n.to !== "/" && pathname.startsWith(n.to)));
        const isOpen = openGroups.includes(g.group) || hasActive;
        return (
          <div key={g.group} className="mt-1">
            <button
              onClick={() => toggle(g.group)}
              data-testid={`navgroup-${g.group.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="w-full flex items-center gap-2 px-5 py-2 label-xs text-white/40 hover:text-white/80 transition-colors duration-150"
            >
              <ChevronRight size={12} className={`transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
              <g.icon size={13} />
              <span className="flex-1 text-left">{g.group}</span>
            </button>
            {isOpen && <div className="pb-1">{g.items.map(link)}</div>}
          </div>
        );
      })}
    </nav>
  );
};



export const Shell = ({ children, title, subtitle, actions }) => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        data-testid="sidebar"
        className={`fixed z-40 inset-y-0 left-0 w-[248px] bg-[#0B0D10] text-white flex flex-col transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src={FIRM.logo} alt="" className="w-9 h-9 object-contain bg-white p-0.5" data-testid="firm-logo" />
            <div className="min-w-0">
              <div className="display text-sm font-black leading-tight">{FIRM.name}</div>
              <div className="label-xs text-white/40 mt-1">Order &amp; Dispatch Ops</div>
            </div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="close-sidebar-btn">
            <X size={18} />
          </button>
        </div>
        <SidebarNav user={user} pathname={loc.pathname} onNavigate={() => setOpen(false)} />
        <div className="px-5 py-4 border-t border-white/10">
          <div className="text-sm font-semibold" data-testid="current-user-name">{user?.name}</div>
          <div className="label-xs text-white/40">{rolesOf(user).join(" + ") || user?.role}</div>
          <div className="mt-3 flex items-center gap-4">
            <button
              onClick={() => setPwOpen(true)}
              data-testid="change-password-btn"
              className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors duration-150"
            >
              <KeyRound size={14} /> Password
            </button>
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors duration-150"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      <ChangePasswordDialog open={pwOpen} setOpen={setPwOpen} />

      <div className="flex-1 lg:ml-[248px] min-w-0">
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-border">
          <div className="px-4 sm:px-8 py-4 sm:py-5 flex flex-wrap items-center gap-x-5 gap-y-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-sidebar-btn">
              <Menu size={20} />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold tracking-tight leading-none truncate">{title}</h1>
              {subtitle && <p className="text-sm text-muted-foreground mt-1 hidden sm:block">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-2.5 flex-wrap justify-end"><GlobalSearch /><SessionPicker />{actions}</div>
          </div>
          <SessionBanner />
        </header>
        <main className="p-4 sm:p-8 lg:px-10 lg:py-9 space-y-6">{children}</main>
      </div>
    </div>
  );
};

export const Stat = ({ label, value, tone = "default", testid }) => {
  const tones = {
    default: "text-foreground",
    primary: "text-[#0055FF]",
    warn: "text-[#DD6B20]",
    bad: "text-[#E53E3E]",
    good: "text-[#38A169]",
  };
  return (
    <div
      data-testid={testid}
      className="panel p-5 hover:shadow-md transition-shadow duration-200"
    >
      <div className="label-xs">{label}</div>
      <div className={`mono text-3xl font-bold mt-2 tabular-nums ${tones[tone]}`}>{value ?? "—"}</div>
    </div>
  );
};

export const Pill = ({ status }) => (
  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-bold ${statusPill(status)}`}>
    {statusLabel(status)}
  </span>
);
